import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useEffect, useMemo } from 'react';

import { useCurrentInstance } from '@/api/organization';
import { DashboardLayout, type DashboardType } from '@/components/Dashboard';
import { usePageFilterContext } from '@/components/page';
import { SupportLink } from '@/components/typography';
import WidgetLibrary from '@/features/WidgetLibrary';
import { ROUTES } from '@/router';

const DashboardContent = () => {
  const { setPageFilters } = usePageFilterContext();
  const { instance } = useCurrentInstance();
  const tamAudience = instance?.tam?.audience ?? undefined;

  const dashboard = useMemo<DashboardType>(
    () => ({
      id: 'dashboard',
      widgets: [
        {
          id: '0',
          type: 'cell',
          title: t`Pipeline Trends`,
          library: WidgetLibrary.PIPELINE_TRENDS_WIDGET,
          showNotes: true,
          gridPos: { x: 0, y: 0, w: 12, h: 6 },
          verifyC99Tag: true,
        },
        {
          id: '1',
          type: 'cell',
          title: t`Visit Efficiency`,
          library: WidgetLibrary.TRAFFIC_EFFICIENCY_WIDGET,
          showNotes: true,
          gridPos: { x: 12, y: 0, w: 12, h: 6 },
          infoPopover: {
            body: (
              <Trans>
                Visit efficiency is the ratio of target audience visits to total visits (unknown
                removed) to give a view of how well the audience is being targeted.{' '}
                <SupportLink variant="body2" color="static-green" path="VISIT_EFFICIENCY">
                  More Details
                </SupportLink>
              </Trans>
            ),
          },
        },
        {
          id: '2',
          type: 'cell',
          title: t`Direct View-Through Reallocation`,
          library: WidgetLibrary.DIRECT_VIEW_THROUGH_REALLOCATION_WIDGET,
          infoPopover: {
            body: (
              <Trans>
                View-Through Reallocation helps attribute a portion of visits originally labeled as
                "Direct" to marketing channels that influenced the user through ad impressions (not
                clicks). This provides a more accurate view of channel performance by crediting
                visits that were likely driven by unclicked, yet seen, campaigns.{' '}
                <SupportLink
                  variant="body2"
                  color="static-green"
                  path="DIRECT_VIEW_THROUGH_REALLOCATION"
                >
                  Learn more
                </SupportLink>
              </Trans>
            ),
          },
          link: {
            url: ROUTES.vendorById('vndr_direct'),
            title: t`Direct Vendor`,
          },
          gridPos: { x: 0, y: 6, w: 12, h: 6 },
        },
        {
          id: '3',
          type: 'cell',
          title: t`Traffic Summary`,
          library: WidgetLibrary.TRAFFIC_TRENDS_WIDGET,
          link: {
            url: ROUTES.webTraffic.path,
            title: t`Web Traffic`,
          },
          showNotes: true,
          gridPos: { x: 12, y: 6, w: 12, h: 6 },
          verifyC99Tag: true,
        },
        {
          id: '4',
          type: 'cell',
          title: t`Pipeline Creation`,
          library: WidgetLibrary.OPPORTUNITY_INFLUENCE_TREND_WIDGET,
          link: {
            url: ROUTES.attribution.path,
            title: t`Attribution`,
          },
          gridPos: { x: 0, y: 12, w: 12, h: 6 },
        },
        {
          id: '5',
          type: 'cell',
          title: t`Closed Won`,
          library: WidgetLibrary.CLOSED_WON_WIDGET,
          link: {
            url: ROUTES.opportunities.path,
            title: t`Opportunities`,
          },
          gridPos: { x: 12, y: 12, w: 12, h: 10 },
        },
        {
          id: '6',
          type: 'cell',
          title: t`Benchmarks`,
          library: WidgetLibrary.BENCHMARKS_WIDGET,
          link: {
            url: ROUTES.webTrafficBenchmarks.path,
            title: t`Benchmarks`,
          },
          gridPos: { x: 0, y: 18, w: 12, h: 10 },
        },
      ],
    }),
    [],
  );

  useEffect(() => {
    setPageFilters({ audience: tamAudience });
  }, [setPageFilters, tamAudience]);

  return <DashboardLayout dashboard={dashboard} isEditable={false} />;
};

export default DashboardContent;
